import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { Property } from '../types';
import { propertyService } from '../services/propertyService';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import PropertyMap from '../components/PropertyMap';

const PropertyDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { canInvest } = useUser();
  const [property, setProperty] = useState<Property | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('details');
  const [investmentAmount, setInvestmentAmount] = useState(100);

  const investmentStatus = canInvest();

  useEffect(() => {
    if (id) {
      fetchProperty(id);
    }
  }, [id]);

  const fetchProperty = async (propertyId: string) => {
    try {
      setLoading(true);
      const data = await propertyService.getProperty(propertyId);
      setProperty(data);
    } catch (err) {
      setError('Failed to load property details');
      console.error('Error fetching property:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInvestClick = () => {
    if (!investmentStatus.canInvest) {
      navigate('/login');
      return;
    }
    navigate(`/invest/${id}`);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getCompletionPercentage = () => {
    if (!property?.total_shares || !property?.available_shares) return 0;
    return ((property.total_shares - property.available_shares) / property.total_shares * 100);
  };

  const getRemainingAmount = () => {
    if (!property?.price_per_share || !property?.available_shares) return 0;
    return property.price_per_share * property.available_shares;
  };

  // Enhanced mock property data for The Cyrus
  const mockProperty: Property = {
    id: id || 'cyrus-phoenix',
    title: 'The Cyrus',
    description: `The Cyrus is a stunning, brand-new construction home that perfectly blends modern design with everyday comfort. Its bright, open-concept layout is enhanced by stylish LVP flooring, recessed lighting, and an abundance of natural light throughout.

The beautifully appointed kitchen features stainless steel appliances, granite countertops, a spacious corner pantry, and a large island with bar seating—ideal for both everyday living and entertaining.

The elegant primary suite offers a luxurious ensuite bath with dual granite vanities, a glass-enclosed shower, a generous walk-in closet, and ample built-in shelving for additional storage.

Step outside to enjoy the covered walkout patio, complete with an outdoor ceiling fan—perfect for relaxing year-round.`,
    address: '1234 Desert View Lane',
    city: 'Phoenix',
    state: 'AZ',
    zip_code: '85001',
    property_type: 'Single Family',
    total_value: 285000,
    total_shares: 11400, // $285k / $25 per share
    available_shares: 2755, // 76% funded
    price_per_share: 25,
    annual_yield: 0.068,
    status: 'active',
    images: ['/api/placeholder/800/600'],
    verification_status: 'verified',
    created_at: '2024-01-01',
    updated_at: '2024-01-01',
    shares_sold: 8645,
    completion_percentage: 76,
    market_cap: 285000,
    stakersCount: 1216
  };

  const displayProperty = property || mockProperty;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/properties')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Properties
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Link 
                to="/"
                className="text-gray-600 hover:text-gray-900 flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </Link>
              <h1 className="text-xl font-bold text-gray-900">{displayProperty.title}</h1>
            </div>
            
            <div className="flex items-center space-x-4">
              <button className="p-2 text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Property Image */}
            <div className="bg-gradient-to-br from-honey-400 to-honey-600 rounded-2xl h-96 flex items-center justify-center">
              <div className="text-white text-center">
                <div className="text-4xl font-bold mb-2">{displayProperty.title}</div>
                <div className="text-xl text-honey-100">{displayProperty.city}, {displayProperty.state}</div>
              </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-2xl border border-gray-200">
              <div className="border-b border-gray-200">
                <nav className="flex space-x-8 px-6">
                  <button
                    onClick={() => setActiveTab('details')}
                    className={`py-4 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'details'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    DETAILS
                  </button>
                  <button
                    onClick={() => setActiveTab('financials')}
                    className={`py-4 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'financials'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    FINANCIALS
                  </button>
                  <button
                    onClick={() => setActiveTab('performance')}
                    className={`py-4 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'performance'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    PERFORMANCE
                  </button>
                </nav>
              </div>

              <div className="p-6">
                {activeTab === 'details' && (
                  <div className="space-y-6">
                    {/* Property Specs */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-gray-900">4</div>
                        <div className="text-sm text-gray-500">Bedrooms</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-gray-900">2</div>
                        <div className="text-sm text-gray-500">Bathrooms</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-gray-900">1,793</div>
                        <div className="text-sm text-gray-500">Sqft</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-gray-900">2025</div>
                        <div className="text-sm text-gray-500">Year Built</div>
                      </div>
                    </div>

                    {/* Description */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Property Description</h3>
                      <div className="text-gray-700 whitespace-pre-line">{displayProperty.description}</div>
                    </div>

                    {/* Highlights */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Property Highlights</h3>
                      <ul className="space-y-2">
                        {[
                          'Brand new construction completed in 2025',
                          'Open-concept layout with modern finishes',
                          'Granite countertops and stainless steel appliances',
                          'Covered outdoor patio with ceiling fan',
                          'Located in growing Phoenix metro area',
                          'Professional property management included',
                        ].map((highlight, index) => (
                          <li key={index} className="flex items-start">
                            <span className="text-green-500 mr-2">✓</span>
                            <span className="text-gray-700">{highlight}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Location Map */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Location</h3>
                      <PropertyMap
                        address={displayProperty.address}
                        city={displayProperty.city}
                        state={displayProperty.state}
                        zipCode={displayProperty.zip_code}
                        propertyName={displayProperty.title}
                        className="mb-4"
                      />
                      <p className="text-sm text-gray-600">
                        {displayProperty.address}, {displayProperty.city}, {displayProperty.state} {displayProperty.zip_code}
                      </p>
                    </div>
                  </div>
                )}

                {activeTab === 'financials' && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Expected Annual Return</h4>
                        <div className="text-2xl font-bold text-green-600">{(displayProperty.annual_yield * 100).toFixed(1)}%</div>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Price per Share</h4>
                        <div className="text-2xl font-bold text-gray-900">{formatCurrency(displayProperty.price_per_share)}</div>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3">Investment Details</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total Property Value</span>
                          <span className="font-medium">{formatCurrency(displayProperty.total_value)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total Shares</span>
                          <span className="font-medium">{displayProperty.total_shares.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Available Shares</span>
                          <span className="font-medium">{displayProperty.available_shares.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'performance' && (
                  <div className="text-center py-12">
                    <div className="text-gray-500">Performance data will be available after tenant occupancy</div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Investment Sidebar */}
          <div className="space-y-6">
            {/* Investment Summary */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6">
              <div className="text-center mb-6">
                <div className="text-3xl font-bold text-gray-900 mb-1">
                  {formatCurrency(getRemainingAmount())}
                </div>
                <div className="text-gray-500">Remaining Investment Amount</div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="text-center">
                  <div className="text-xl font-bold text-gray-900">{Math.round(getCompletionPercentage())}%</div>
                  <div className="text-sm text-gray-500">Funded</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-gray-900">{(displayProperty.stakersCount || 0).toLocaleString()}</div>
                  <div className="text-sm text-gray-500">Investors</div>
                </div>
              </div>

              {/* Funding Progress Bar */}
              <div className="mb-6">
                <div className="bg-gray-200 rounded-full h-3">
                  <div 
                    className="bg-blue-600 h-3 rounded-full" 
                    style={{ width: `${getCompletionPercentage()}%` }}
                  />
                </div>
              </div>

              {/* Investment Amount Input */}
              {investmentStatus.canInvest ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Investment Amount
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                      <input
                        type="number"
                        value={investmentAmount}
                        onChange={(e) => setInvestmentAmount(Number(e.target.value))}
                        min={100}
                        max={1000}
                        step={25}
                        className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Min: $100 • Max: $1,000
                    </div>
                  </div>

                  <button
                    onClick={handleInvestClick}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 px-6 rounded-lg font-semibold transition-colors"
                  >
                    INVEST
                  </button>
                </div>
              ) : (
                <div className="text-center">
                  <div className="bg-yellow-50 text-yellow-800 p-4 rounded-lg mb-4">
                    {investmentStatus.reasons[0]}
                  </div>
                  <Link
                    to="/login"
                    className="block w-full bg-blue-600 hover:bg-blue-700 text-white py-4 px-6 rounded-lg font-semibold transition-colors"
                  >
                    SIGN IN TO INVEST
                  </Link>
                </div>
              )}

              {/* High Demand Notice */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                <div className="text-sm text-blue-800">
                  <p className="font-medium">Due to high demand, we have limited the maximum investment amount in The Cyrus to $1,000 so more people have the opportunity to invest.</p>
                </div>
              </div>
            </div>

            {/* Risk Disclosure */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="font-semibold text-yellow-800 mb-2">Investment Risk Notice</h4>
              <ul className="text-sm text-yellow-700 space-y-1">
                <li>• Real estate investments involve risk of loss</li>
                <li>• Market conditions may affect property value</li>
                <li>• Rental income is not guaranteed</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetail;
